import { CSSProperties, useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
// const nowpayApp = new URL("../assets/images/nowpayApp.webp", import.meta.url).href;
import nowpayApp from '../../shared/assets/images/nowpayAppSaudi.png';
import nowpayAppAr from '../../shared/assets/images/nowpayAppSaudiAr.png';
{/**Icons */}
import person from '../../shared/assets/images/person.svg';
import paper from '../../shared/assets/images/paper.svg';
import percentage from '../../shared/assets/images/percentage.svg';
import system from '../../shared/assets/images/system.svg';
import sharia from '../../shared/assets/images/sharia.svg';
import calender from '../../shared/assets/images/calender.svg';
import clock from '../../shared/assets/images/clock.svg';
import lock from '../../shared/assets/images/lock.svg';
import chat from '../../shared/assets/images/chat.svg';
{/**logos */}
import pepsico from '../../shared/assets/images/logos/pepsico.svg';
import mac from '../../shared/assets/images/logos/mac-client.svg';
import macSmall from '../../shared/assets/images/logos/mac.svg';
import extra from '../../shared/assets/images/logos/extra.webp';
import othaim from '../../shared/assets/images/logos/othaim.svg';
import tamimi from '../../shared/assets/images/logos/tamimi.svg';
import shakerGroup from '../../shared/assets/images/logos/shaker-group.svg';
import budget from '../../shared/assets/images/logos/budget.webp';
import bawan from '../../shared/assets/images/logos/bawan.svg';
import { useLanguage } from '../context/LanguageContext';
import { fadeIn, fadeUp, slideLeft, slideRight, staggerContainer } from "../lib/motion";
import { useNavigate } from "react-router-dom";

type CountUpProps = {
  value: number;
  suffix?: string;
  duration?: number;
  decimals?: number;
  start?: boolean;
};

const CountUp = ({
  value,
  suffix = "",
  duration = 1400,
  decimals = 0,
  start = true,
}: CountUpProps) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (!start) {
      setDisplayValue(0);
      return;
    }

    let startTime: number | null = null;
    let frameId = 0;

    const animate = (timestamp: number) => {
      if (startTime === null) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const currentValue = value * progress;
      setDisplayValue(currentValue);

      if (progress < 1) {
        frameId = requestAnimationFrame(animate);
      }
    };

    frameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameId);
  }, [value, duration, start]);

  const formatted = decimals > 0
    ? displayValue.toFixed(decimals)
    : Math.round(displayValue).toString();

  return (
    <span>
      {formatted}
      {suffix}
    </span>
  );
};

const BenefitsAndImpact = () => {
  const { translation, isRTL, lang } = useLanguage()
  const navigate = useNavigate()
  const statsRef = useRef<HTMLDivElement | null>(null);
  const statsInView = useInView(statsRef, { once: true, amount: 0.3 });

  const companiesBenefits = [
    {
      text: translation.companiesBenefit1,
      image: person
    },
    {
      text: translation.companiesBenefits2,
      image: paper
    },
    {
      text: translation.companiesBenefits3,
      image: percentage
    },
    {
      text: translation.companiesBenefits4,
      image: system
    },
    {
      text: translation.companiesBenefits5,
      image: sharia
    }
  ]

  const teamsBenefits = [
    {
      text: translation.reduceStress,
      image: calender
    },
    {
      text: translation.customerSupport,
      image: clock
    },
    {
      text: translation.secureTransactions,
      image: lock
    },
    {
      text: translation.teamsBenefits1,
      image: chat
    },
  ]

  const clientLogosLtr = [
    { src: pepsico, alt: "pepsico", className: "max-h-[99px]" },
    { src: mac, alt: "mac", className: "max-h-[95px]" },
    { src: extra, alt: "eXtra", className: "max-h-[102px]" },
    { src: othaim, alt: "Othaim", className: "max-h-[112px]" },
    { src: tamimi, alt: "tamimi", className: "max-h-[40px]" },
    { src: shakerGroup, alt: "Shaker Group", className: "max-h-[94px]" },
    { src: bawan, alt: "Bawan", className: "max-h-[64px]" },
    { src: budget, alt: "Budget", className: "max-h-[64px]" },
  ]

  const clientLogoRows = [
    [clientLogosLtr.slice(0, 2), clientLogosLtr.slice(2, 4)],
    [clientLogosLtr.slice(4, 6), clientLogosLtr.slice(6, 8)],
  ]

  return (
    <main>
      <motion.div
        className="px-[1.25rem] xl:px-[7.75rem] flex flex-col items-center lg:gap-[3.25rem]"
        variants={staggerContainer(0.12, 0.08)}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.3 }}
      >
        <motion.div className="text-2xl md:text-[2.5rem] leading-[100%] font-bold text-primary text-center" variants={fadeUp}>
          {translation.whatsInIt}
        </motion.div>
        <div className="flex flex-col md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-16 xl:flex xl:flex-row items-center md:items-start">
          <motion.div className="flex flex-col gap-3 md:gap-6 pt-10 text-start w-full max-w-[18.75rem] xl:hidden" variants={staggerContainer(0.1, 0.05)}>
            <motion.div className="text-2xl md:text-[1.625rem] font-bold text-primary text-center md:text-start" variants={fadeUp}>
              {translation.teams}
            </motion.div>
            {teamsBenefits.map((benefit, index) => (
              <motion.div
                key={index}
                className="flex gap-3.5 md:gap-[1.125rem] text-blackTwo"
                variants={fadeUp}
              >
                <img src={benefit.image} alt="" className='w-9 h-9 md:w-[3.125rem] md:h-[3.125rem] hover-scale'/>
                <div className="text-base lg:text-[1.375rem] !leading-[130%] lg:leading-[1.875rem] hover-text-rise my-auto">{benefit.text}</div>
              </motion.div>
            ))}
          </motion.div>
          <motion.div className="flex flex-col gap-3 md:gap-6 pt-10 text-start lg:text-end w-full max-w-[18.75rem]" variants={staggerContainer(0.1, 0.05)}>
            <motion.div className="text-2xl md:text-[1.625rem] font-bold text-primary text-center md:text-end" variants={fadeUp}>
              {translation.companies}
            </motion.div>
            {companiesBenefits.map((benefit, index) => (
              <motion.div
                key={index}
                className="flex gap-3.5 md:gap-[1.125rem] text-blackTwo"
                variants={fadeUp}
              >
                <img src={benefit.image} alt="" className='w-9 h-9 md:w-[3.125rem] md:h-[3.125rem] xl:hidden hover-scale'/>
                <div className="text-base lg:text-[1.375rem] !leading-[130%] lg:leading-[1.875rem] hover-text-rise my-auto">{benefit.text}</div>
                <img src={benefit.image} alt="" className='hidden xl:block w-[3.125rem] h-[3.125rem] hover-scale'/>
              </motion.div>
            ))}
          </motion.div>
          <motion.img
            src={isRTL ? nowpayAppAr : nowpayApp}
            alt="nowpay app"
            className='w-[16.5rem] md:w-[26.75rem] h-[33.5rem] md:h-[54.375rem] mt-12 md:mt-0 md:col-span-2 xl:col-auto md:justify-self-center drop-shadow-[0px_10px_15px_#004E6440] animate-float'
            variants={slideLeft}
          />
          <motion.div className="hidden xl:flex flex-col gap-3 md:gap-6 pt-10 text-start w-full max-w-[18.75rem]" variants={staggerContainer(0.1, 0.05)}>
            <motion.div className="text-2xl md:text-[1.625rem] font-bold text-primary text-center md:text-start" variants={fadeUp}>
              {translation.teams}
            </motion.div>
            {teamsBenefits.map((benefit, index) => (
              <motion.div
                key={index}
                className="flex gap-[1.125rem] text-blackTwo"
                variants={fadeUp}
              >
                <img src={benefit.image} alt="" className='w-9 h-9 md:w-[3.125rem] md:h-[3.125rem] hover-scale'/>
                <div className="text-base lg:text-[1.375rem] !leading-[130%] lg:leading-[1.875rem] hover-text-rise my-auto">{benefit.text}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.div>
      <motion.div
        ref={statsRef}
        className='flex flex-col justify-center items-center gap-12 lg:gap-[5.625rem] w-full -mt-24 xl:-mt-[6.25rem] pt-36 pb-12 lg:pb-[6.25rem] rounded-[1.25rem] lg:rounded-[1.875rem] bg-secondary'
        variants={staggerContainer(0.12, 0.08)}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
      >
        <motion.div className='flex flex-col justify-center items-center gap-3 md:gap-12' variants={fadeIn}>
          <motion.div className='text-2xl md:text-[2.5rem] leading-[100%] font-bold text-center text-primary' variants={fadeUp}>
            {translation.nowpayEffect}
          </motion.div>
          <motion.div className='flex flex-col lg:flex-row gap-6 md:gap-14' variants={staggerContainer(0.12, 0.05)}>
            <motion.div
              className='flex flex-col gap-2.5 md:gap-5 w-[14.5rem] items-center justify-start'
              style={{ "--stagger-index": 1 } as CSSProperties}
              variants={fadeUp}
            >
              <div className='text-[2.875rem] leading-[100%] font-bold text-softGreen font-sans'>
                <CountUp value={300} suffix="+" duration={1500} start={statsInView} />
              </div>
              <div className='text-base md:text-[1.375rem] leading-[130%] text-center text-blackTwo'>{translation.companiesDesc}</div>
            </motion.div>
            <motion.div
              className='flex flex-col gap-2.5 md:gap-5 w-[14rem] items-center justify-start'
              style={{ "--stagger-index": 2 } as CSSProperties}
              variants={fadeUp}
            >
              <div className='text-[2.875rem] leading-[100%] font-bold text-softGreen font-sans'>
                <CountUp value={1.5} suffix="B+" duration={1700} decimals={1} start={statsInView} />
              </div>
              <div className='text-base md:text-[1.375rem] leading-[130%] text-center text-blackTwo'>{translation.paymentsDesc}</div>
            </motion.div>
            <motion.div
              className='flex flex-col gap-2.5 md:gap-5 w-[14.5rem] items-center justify-start'
              style={{ "--stagger-index": 3 } as CSSProperties}
              variants={fadeUp}
            >
              <div className='text-[2.875rem] leading-[100%] font-bold text-softGreen font-sans'>
                <CountUp value={300} suffix="K+" duration={1500} start={statsInView} />
              </div>
              <div className='text-base md:text-[1.375rem] leading-[130%] text-center text-blackTwo'>{translation.peopleDesc}</div>
            </motion.div>
          </motion.div>
        </motion.div>
        <motion.div
          className='w-full sm:max-w-xl lg:max-w-4xl flex flex-col justify-center items-stretch md:items-center gap-8 md:gap-[4.375rem] xl:gap-[3.75rem] px-[20px] md:px-[1.25rem] xl:px-0'
          dir={isRTL ? "rtl" : "ltr"}
          variants={staggerContainer(0.1, 0.05)}
        >
          <motion.div
            className="grid grid-cols-2 gap-x-[26px] gap-y-[40px] w-full md:hidden"
            variants={staggerContainer(0.1, 0.05)}
          >
            {clientLogosLtr.map((logo, index) => {
              const staggerIndex = index + 1
              const mobileLogoClassName = `max-w-[7.75rem] ${logo.className} object-contain ${index === 0 ? "object-top" : "object-center"} hover-scale`
              return (
                <motion.div
                  key={logo.alt}
                  className="staggered-pop flex items-center justify-center w-full min-w-0"
                  style={{ "--stagger-index": staggerIndex } as CSSProperties}
                  variants={fadeUp}
                >
                  {logo.alt === "mac" ? (
                    <img src={macSmall} alt={logo.alt} className={mobileLogoClassName} />
                  ) : (
                    <img src={logo.src} alt={logo.alt} className={mobileLogoClassName} />
                  )}
                </motion.div>
              )
            })}
          </motion.div>
          {clientLogoRows.map((row, rowIndex) => (
            <motion.div
              key={rowIndex}
              className={`hidden md:flex flex-col md:flex-row gap-8 md:gap-14 lg:gap-20 xl:gap-[6rem] ${rowIndex === 1 ? "items-center justify-center" : "items-start justify-between"}`}
            >
              {(row).map((group, groupIndex) => (
                <motion.div
                  key={groupIndex}
                  className={`flex gap-8 lg:gap-14 xl:gap-[6rem] ${rowIndex === 1 ? "items-center justify-center" : "items-start justify-between"}`}
                >
                  {group.map((logo, logoIndex) => {
                    const staggerIndex = rowIndex * 4 + groupIndex * 2 + logoIndex + 1
                    const logoClassName = `object-contain block ${rowIndex === 0 && logoIndex === 0 ? "object-top h-[6.125rem] sm:h-auto" : "object-center h-[7rem] sm:h-auto"} ${rowIndex === 0 ? 'lg:max-w-[11rem] lg:max-h-[12rem] xl:w-[8.375rem] xl:h-[8.5rem] xl:max-w-[8.375rem] xl:max-h-[8.5rem]' : 'lg:max-w-[12.5rem] xl:w-[8.375rem] xl:max-w-[8.375rem] lg:min-h-max'} max-w-[7rem] hover-scale`
                    return (
                      <motion.div
                        key={logo.alt}
                        className="staggered-pop flex items-center justify-center"
                        style={{ "--stagger-index": staggerIndex } as CSSProperties}
                        variants={fadeUp}
                      >
                        <img src={logo.src} alt={logo.alt} className={logoClassName} />
                      </motion.div>
                    )
                  })}
                </motion.div>
              ))}
            </motion.div>
          ))}
        </motion.div>
        <motion.button
          className={`h-11 lg:h-[3.625rem] inline-flex items-center justify-center bg-softGreen text-white px-[3.75rem] py-0 rounded-[6.25rem] text-[1.125rem] lg:text-[1.375rem] font-semibold hover:bg-[#66B57C] transition-colors duration-300 hover-glow leading-normal ${lang === "ar" ? "font-arabic" : ""}`}
          variants={slideRight}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => navigate("/our-client")}
        >
          {translation.ourClients}
        </motion.button>
      </motion.div>
    </main>
  )
}

export default BenefitsAndImpact
